import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import i18next from "i18next";
import {Tooltip} from "@mui/material";
import TablesComponent from "../Tables/Tables.Component";
import {GetVacantMatchingList} from "../../Services";
import {showError} from "../../Helpers";
import {DEFAULT_BRAND_FALLBACK_HEX} from "../../utils/contrastColor.utility";
import "./VacantMatchingTable.Style.scss";

const MATCHING_COLUMNS_CONFIG = [
  {key: "position_code", label: "position-code", isMultiLang: false},
  {key: "job_title_code", label: "job-title-code", isMultiLang: false},
  {key: "job_title_name", label: "job-title-name", isMultiLang: true},
  {key: "org_code", label: "org-code", isMultiLang: false},
  {key: "org_name", label: "org-name", isMultiLang: true},
  {key: "grade_code", label: "grade-code", isMultiLang: false},
  {key: "employment_type", label: "employment-type", isMultiLang: true},
];

export const VacantMatchingTable = ({
  parentTranslationPath,
  vacantUuid,
  branchConfigUuid,
  transcationType,
  isFetchEnabled = true,
  isRequestableOnly = false,
  uniqueKeyInput,
  mapRow,
  isRowHighlighted,
  onSelectCheckboxChanged,
  localSelectedRowsController,
}) => {
  const {t} = useTranslation(parentTranslationPath);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState({limit: 10, page: 1});
  const [matching, setMatching] = useState(() => ({
    results: [],
    totalCount: 0,
  }));

  const getMatchingList = useCallback(async () => {
    setIsLoading(true);
    const response = await GetVacantMatchingList({
      ...filter,
      vacant_uuid: vacantUuid,
      branch_config_uuid: branchConfigUuid,
      transcation_type: transcationType,
    });
    setIsLoading(false);
    if (response && response.status === 200)
      setMatching({
        results: response.data?.results ?? [],
        totalCount: response.data?.paginate?.total ?? 0,
      });
    else {
      setMatching({results: [], totalCount: 0});
      showError(t("Shared:failed-to-get-saved-data"), response);
    }
  }, [filter, vacantUuid, branchConfigUuid, transcationType, t]);

  useEffect(() => {
    if (isFetchEnabled) getMatchingList();
  }, [isFetchEnabled, getMatchingList]);

  const rows = useMemo(
    () => (mapRow ? matching.results.map(mapRow) : matching.results),
    [matching.results, mapRow]
  );

  // a row the backend marks as not requestable can't be selected, a missing
  // can_request is treated as requestable
  const getIsDisabledRow = useCallback(
    (row) => isRequestableOnly && row?.can_request === false,
    [isRequestableOnly]
  );

  const hasDisabledRows = useMemo(
    () => rows.some((row) => getIsDisabledRow(row)),
    [rows, getIsDisabledRow]
  );

  const headerData = useMemo(
    () =>
      MATCHING_COLUMNS_CONFIG.map((column, columnIndex) => ({
        isHidden: false,
        input: column.key,
        label: t(column.label),
        component: (row) => {
          const value = (
            <span className="vacant-matching-cell-value">
              {column.isMultiLang
                ? row?.[column.key]?.[i18next.language] ||
                  row?.[column.key]?.en ||
                  ""
                : row?.[column.key] ?? ""}
            </span>
          );
          if (columnIndex !== 0 || !getIsDisabledRow(row)) return value;

          return (
            <span className="d-inline-flex-v-center">
              <Tooltip title={t("position-not-requestable-tooltip")} arrow>
                <span className="vacant-matching-lock mdi mdi-lock-outline" />
              </Tooltip>
              {value}
            </span>
          );
        },
      })),
    [t, getIsDisabledRow]
  );

  const onPageIndexChanged = useCallback((newIndex) => {
    setFilter((items) => ({...items, page: newIndex + 1}));
  }, []);

  const onPageSizeChanged = useCallback((newPageSize) => {
    setFilter((items) => ({...items, page: 1, limit: newPageSize}));
  }, []);

  const rowStyles = useCallback(
    (row) => {
      const styles = {
        ...(isRowHighlighted?.(row) && {
          "& .MuiTableCell-body": {
            backgroundColor: `color-mix(in srgb, var(--bg-primary, var(--primary, ${DEFAULT_BRAND_FALLBACK_HEX})) 8%, transparent) !important`,
          },
        }),
        ...(getIsDisabledRow(row) && {
          "& .vacant-matching-cell-value": {opacity: 0.5},
        }),
      };

      return Object.keys(styles).length ? styles : undefined;
    },
    [isRowHighlighted, getIsDisabledRow]
  );

  return (
    <div className="vacant-matching-table-wrapper">
      {hasDisabledRows && (
        <div className="vacant-matching-notice">
          <span className="mdi mdi-information-outline" />
          <span>{t("positions-not-requestable-notice")}</span>
        </div>
      )}
      <TablesComponent
        isWithCheckAll
        isWithCheck
        isWithoutBoxWrapper
        data={rows}
        headerData={headerData}
        pageIndex={filter.page - 1}
        pageSize={filter.limit}
        totalItems={matching.totalCount}
        isLoading={isLoading}
        onSelectCheckboxChanged={onSelectCheckboxChanged}
        localSelectedRowsController={localSelectedRowsController}
        uniqueKeyInput={uniqueKeyInput}
        getIsDisabledRow={isRequestableOnly ? getIsDisabledRow : undefined}
        onPageIndexChanged={onPageIndexChanged}
        onPageSizeChanged={onPageSizeChanged}
        parentTranslationPath={parentTranslationPath}
        rowStyles={rowStyles}
      />
    </div>
  );
};

VacantMatchingTable.propTypes = {
  parentTranslationPath: PropTypes.string,
  vacantUuid: PropTypes.string,
  branchConfigUuid: PropTypes.string,
  transcationType: PropTypes.number,
  isFetchEnabled: PropTypes.bool,
  // when true, rows with can_request === false are disabled and explained
  isRequestableOnly: PropTypes.bool,
  uniqueKeyInput: PropTypes.string.isRequired,
  mapRow: PropTypes.func,
  isRowHighlighted: PropTypes.func,
  onSelectCheckboxChanged: PropTypes.func,
  localSelectedRowsController: PropTypes.arrayOf(PropTypes.object),
};
